import React from "react";

import ReactDOM from "react-dom/client";

import * as Sentry from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { toast } from "sonner";

import { APP_CONFIG } from "./config/app";
import { initializeApiClient } from "./core/api/api-client";
import { AuthProvider, useAuth } from "./core/auth/auth";
import { initializeUserPermissions } from "./core/auth/auth-service";
import "./i18n/config";
import { routeTree } from "./routeTree.gen";
import "./styles/style.css";
import "./styles/theme-transition.css";

Sentry.init({
  // dsn: "https://956846a89af1441c0d601a51c2351517@o921565.ingest.us.sentry.io/4510204016525312",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  enabled: import.meta.env.PROD,
  release: APP_CONFIG.version,
  environment: import.meta.env.PROD ? "production" : "development",
});

window.toast = toast;

// Import theme transition styles
// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: {
    auth: undefined!,
  },
});

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Export the InnerApp component
export function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
}

// Export the App component
export function App() {
  return (
    <AuthProvider>
      <InnerApp />
    </AuthProvider>
  );
}

// Move the rendering logic to a separate function
function renderApp() {
  const rootElement = document.getElementById("app")!;

  if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
}

await initializeApiClient();
// Initialize user permissions if user is already authenticated
await initializeUserPermissions();
renderApp();
